import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
  Copy,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { API_URL } from "../../config";
import { sanitizeHtml } from "../../lib/sanitize";

// Blend a #rrggbb color with white (mirrors backend _tint). ratio = how much of the color shows.
const tintHex = (hex, ratio = 0.08) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#ffffff";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return "#ffffff";
  const mix = (c) => Math.round(c * ratio + 255 * (1 - ratio));
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

export const EmailsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [audience, setAudience] = useState("buyers"); // "buyers" | "users" | "both"
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [waSearch, setWaSearch] = useState("");
  const [waResults, setWaResults] = useState([]);
  const [waSearchOpen, setWaSearchOpen] = useState(false);
  const [waRecipient, setWaRecipient] = useState(null);
  const [waMessage, setWaMessage] = useState("");
  const [failuresModal, setFailuresModal] = useState(null); // { id, subject, failed_recipients:[...] }
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("emailComposeTheme");
      return saved === "light" || saved === "brand" || saved === "dark" ? saved : "dark";
    } catch (_) { return "dark"; }
  });
  const [accentColor, setAccentColor] = useState("#f59e0b");

  // Persist theme preference across sessions
  useEffect(() => {
    try { localStorage.setItem("emailComposeTheme", theme); } catch (_) {}
  }, [theme]);

  useEffect(() => {
    fetchSessions();
    fetchHistory();
    // Load show.accent_color for the Brand theme preview
    axios.get(`${API_URL}/api/show`).then((r) => {
      if (r.data?.accent_color) setAccentColor(r.data.accent_color);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSubscriberCount();
  }, [sessionFilter, statusFilter, audience]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sessions`);
      setSessions(response.data);
    } catch (e) { console.error("Failed to fetch sessions:", e); }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/emails/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (e) { console.error("Failed to fetch email history:", e); }
  };

  const fetchSubscriberCount = async () => {
    try {
      const params = new URLSearchParams();
      if (sessionFilter !== "all") params.append("session_id", sessionFilter);
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      params.append("audience", audience);
      const response = await axios.get(`${API_URL}/api/admin/emails/subscribers?${params}`, { withCredentials: true });
      setSubscriberCount(response.data.count);
    } catch (e) { console.error("Failed to fetch subscriber count:", e); }
  };

  const searchRecipients = async (query) => {
    if (query.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    try {
      const response = await axios.get(`${API_URL}/api/admin/emails/search-recipients?q=${encodeURIComponent(query)}`, { withCredentials: true });
      const filtered = response.data.filter(r => !selectedRecipients.find(s => s.email === r.email));
      setSearchResults(filtered);
      setSearchOpen(filtered.length > 0);
    } catch { setSearchResults([]); }
  };

  const addRecipient = (recipient) => {
    if (!selectedRecipients.find(r => r.email === recipient.email)) {
      setSelectedRecipients(prev => [...prev, recipient]);
    }
    setRecipientSearch("");
    setSearchResults([]);
    setSearchOpen(false);
  };

  const removeRecipient = (email) => {
    setSelectedRecipients(prev => prev.filter(r => r.email !== email));
  };

  const searchWaRecipient = async (query) => {
    if (query.length < 2) { setWaResults([]); setWaSearchOpen(false); return; }
    try {
      const response = await axios.get(`${API_URL}/api/admin/emails/search-recipients?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setWaResults(response.data);
      setWaSearchOpen(response.data.length > 0);
    } catch { setWaResults([]); }
  };

  const buildHtmlEmail = () => {
    return content;
  };

  const handleFileAttach = (e) => {
    const files = Array.from(e.target.files);
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    files.forEach((file) => {
      if (!allowed.includes(file.type)) {
        toast.error(`Ficheiro ${file.name}: tipo não suportado (apenas PDF, JPEG, PNG)`);
        return;
      }
      if (file.size > maxSize) {
        toast.error(`Ficheiro ${file.name}: excede 5MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setAttachments((prev) => [...prev, { filename: file.name, content: base64, type: file.type }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendTest = async () => {
    if (!subject || !content) {
      toast.error("Preencha o assunto e o conteúdo");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/emails/test`, {
        subject,
        html_content: buildHtmlEmail(),
        attachments: attachments.length > 0 ? attachments : undefined,
        theme,
      }, { withCredentials: true });
      toast.success("Email de teste enviado para o seu email!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar teste");
    }
  };

  const handleSend = async () => {
    if (!subject || !content) {
      toast.error("Preencha o assunto e o conteúdo");
      return;
    }
    const totalRecipients = selectedRecipients.length > 0 ? selectedRecipients.length : subscriberCount;
    if (totalRecipients === 0) {
      toast.error("Nenhum destinatário selecionado");
      return;
    }

    // Pre-flight validation for specific recipients (catches typo domains like .cpom)
    if (selectedRecipients.length > 0) {
      try {
        const vr = await axios.post(`${API_URL}/api/admin/emails/validate-recipients`, {
          emails: selectedRecipients.map(r => r.email),
        }, { withCredentials: true });
        if (vr.data.invalid_count > 0) {
          const sample = vr.data.invalid.slice(0, 5).map(x => `• ${x.email} — ${x.error}`).join("\n");
          const ok = window.confirm(
            `⚠️ ${vr.data.invalid_count} email(s) com problemas detectados:\n\n${sample}` +
            (vr.data.invalid_count > 5 ? `\n…e mais ${vr.data.invalid_count - 5}` : "") +
            `\n\nContinuar com os ${vr.data.valid_count} válidos?`
          );
          if (!ok) return;
        }
      } catch (_) { /* non-blocking */ }
    }

    setSending(true);
    try {
      const payload = {
        subject,
        html_content: buildHtmlEmail(),
        attachments: attachments.length > 0 ? attachments : undefined,
        theme,
      };
      if (selectedRecipients.length > 0) {
        payload.specific_emails = selectedRecipients.map(r => r.email);
      } else {
        payload.session_id = sessionFilter !== "all" ? sessionFilter : null;
        payload.status_filter = statusFilter !== "all" ? statusFilter : null;
        payload.audience = audience;
      }
      const response = await axios.post(`${API_URL}/api/admin/emails/send`, payload, { withCredentials: true });
      const { sent, failed } = response.data;
      if (failed > 0) {
        toast.warning(`${sent} enviados · ${failed} falharam — abra o histórico para ver detalhes`);
      } else {
        toast.success(response.data.message);
      }
      setSubject("");
      setContent("");
      setAttachments([]);
      setSelectedRecipients([]);
      // Note: theme intentionally NOT reset — admin's preference is sticky.
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar emails");
    } finally {
      setSending(false);
    }
  };

  const openFailures = async (campaign) => {
    setLoadingFailures(true);
    setFailuresModal({ id: campaign.id, subject: campaign.subject, failed_recipients: [] });
    try {
      const r = await axios.get(`${API_URL}/api/admin/emails/history/${campaign.id}/failures`, { withCredentials: true });
      setFailuresModal({ id: campaign.id, subject: r.data.subject, failed_recipients: r.data.failed_recipients || [] });
    } catch (e) {
      toast.error("Erro ao carregar falhas");
      setFailuresModal(null);
    } finally { setLoadingFailures(false); }
  };

  const retryFailed = async () => {
    if (!failuresModal?.id) return;
    setRetrying(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/emails/history/${failuresModal.id}/retry`, {}, { withCredentials: true });
      toast.success(r.data.message);
      setFailuresModal({ ...failuresModal, failed_recipients: r.data.failed_recipients || [] });
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reenviar");
    } finally { setRetrying(false); }
  };

  const exportFailuresCsv = () => {
    if (!failuresModal?.failed_recipients?.length) return;
    const rows = ["email,motivo", ...failuresModal.failed_recipients.map(f => `${f.email},"${(f.error || "").replace(/"/g, '""')}"`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `falhas-${(failuresModal.subject || "campanha").slice(0, 24).replace(/\s+/g, "_")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const duplicateCampaign = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/emails/history/${id}`, { withCredentials: true });
      const c = r.data || {};
      setSubject(c.subject || "");
      setContent(c.html_content || "");
      setAttachments([]); // attachments are not preserved between campaigns
      setSelectedRecipients([]);
      setSessionFilter("all");
      setStatusFilter("all");
      setTheme(c.theme || "dark");
      toast.success("Campanha carregada no editor — pode editar e enviar");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar campanha");
    }
  };

  return (
    <div className="space-y-6" data-testid="emails-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Gestão de Comunicação</h1>
        <p className="text-zinc-500 text-sm mt-1">Gerir a Comunicação: emails e Whatsapp</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Email */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-semibold text-white mb-4">Compor Email</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Assunto</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="input-dark mt-2"
                  placeholder="Ex: Informação importante sobre o evento"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Tema do Email</Label>
                <div className="mt-2 grid grid-cols-3 gap-2" data-testid="email-theme-selector">
                  <button
                    type="button"
                    onClick={() => setTheme("dark")}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                      theme === "dark"
                        ? "border-amber-500 bg-amber-500/10 text-amber-200"
                        : "border-white/10 bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    }`}
                    data-testid="email-theme-dark"
                  >
                    <span className="inline-block w-4 h-4 rounded-full border border-white/20" style={{ background: "linear-gradient(135deg,#18181b,#27272a)" }} />
                    Escuro
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme("light")}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                      theme === "light"
                        ? "border-amber-500 bg-amber-500/10 text-amber-200"
                        : "border-white/10 bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    }`}
                    data-testid="email-theme-light"
                  >
                    <span className="inline-block w-4 h-4 rounded-full border border-zinc-300" style={{ background: "#ffffff" }} />
                    Claro
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme("brand")}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                      theme === "brand"
                        ? "border-amber-500 bg-amber-500/10 text-amber-200"
                        : "border-white/10 bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    }`}
                    data-testid="email-theme-brand"
                  >
                    <span className="inline-block w-4 h-4 rounded-full border" style={{ background: accentColor, borderColor: accentColor }} />
                    Marca
                  </button>
                </div>
                {theme === "brand" && (
                  <p className="text-zinc-500 text-xs mt-2">
                    Usa automaticamente a cor da marca <span className="font-mono" style={{ color: accentColor }}>{accentColor}</span> definida nas Configurações.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">Conteúdo</Label>
                <div className="mt-2">
                  <RichTextEditor
                    value={content}
                    onChange={(html) => setContent(html)}
                    placeholder="Escreva aqui o conteúdo do email..."
                  />
                </div>
              </div>
              {/* Attachments */}
              <div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 bg-zinc-800/50 text-zinc-300 text-sm cursor-pointer hover:bg-zinc-800 transition-colors">
                    <Paperclip className="w-4 h-4" />
                    Anexar Ficheiro
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={handleFileAttach}
                      className="hidden"
                      data-testid="email-attach-input"
                    />
                  </label>
                  <span className="text-zinc-500 text-xs">PDF, JPEG, PNG (máx 5MB)</span>
                </div>
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {attachments.map((att, i) => (
                      <div key={`${att.filename}-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 border border-white/10 text-sm">
                        <Paperclip className="w-3 h-3 text-zinc-400" />
                        <span className="text-zinc-300">{att.filename}</span>
                        <button
                          onClick={() => removeAttachment(i)}
                          className="text-red-400 hover:text-red-300 ml-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {showPreview && content && (() => {
                const previewBg = theme === "light" ? "#f4f4f5" : theme === "brand" ? tintHex(accentColor, 0.08) : "#09090b";
                const previewCard = theme === "dark" ? "linear-gradient(135deg,#18181b,#27272a)" : "#ffffff";
                const previewText = theme === "dark" ? "#fafafa" : theme === "brand" ? "#1f2937" : "#18181b";
                const previewBorder =
                  theme === "brand"
                    ? accentColor
                    : theme === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.08)";
                const themeLabel = theme === "brand" ? "Marca" : theme === "light" ? "Claro" : "Escuro";
                return (
                  <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="email-preview">
                    <p className="text-zinc-500 text-xs px-4 py-2 bg-zinc-800/50 border-b border-white/10">
                      Pré-visualização — tema {themeLabel}
                    </p>
                    <div
                      className="p-6"
                      style={{ background: previewBg, color: previewText }}
                    >
                      <div
                        style={{
                          maxWidth: 500,
                          margin: "0 auto",
                          background: previewCard,
                          borderRadius: 16,
                          padding: 24,
                          border: `1px solid ${previewBorder}`,
                          color: previewText,
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
                        <p className="text-center text-xs mt-4" style={{ color: accentColor }}>&copy; BILHETEZ</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowPreview(!showPreview)}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="email-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? "Fechar" : "Pré-visualizar"}
                </Button>
                <Button
                  onClick={handleSendTest}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="email-send-test-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Teste
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      disabled={sending || !subject || !content || (selectedRecipients.length === 0 && subscriberCount === 0)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="email-send-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {sending ? "A enviar..." : `Enviar para ${selectedRecipients.length > 0 ? selectedRecipients.length : subscriberCount} emails`}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-900 border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Confirmar Envio</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        Tem a certeza que quer enviar este email para {selectedRecipients.length > 0 ? selectedRecipients.length : subscriberCount} destinatários?
                        Esta ação não pode ser revertida.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSend} className="bg-green-600 hover:bg-green-700 text-white">
                        Confirmar Envio
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Info */}
        <div className="space-y-4">
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-semibold text-white mb-4">Destinatários</h2>
            <div className="space-y-4">

              {/* Individual Recipient Search */}
              <div>
                <Label className="text-zinc-300">Procurar Pessoa</Label>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={recipientSearch}
                    onChange={(e) => { setRecipientSearch(e.target.value); searchRecipients(e.target.value); }}
                    className="input-dark pl-9"
                    placeholder="Nome ou email..."
                    data-testid="email-recipient-search"
                  />
                  {searchOpen && searchResults.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-zinc-800 border border-white/10 rounded-lg max-h-48 overflow-y-auto shadow-xl">
                      {searchResults.map((r) => (
                        <button
                          key={r.email}
                          onClick={() => addRecipient(r)}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-700 transition-colors border-b border-white/5 last:border-0"
                          data-testid={`recipient-option-${r.email}`}
                        >
                          <p className="text-white text-sm">{r.name}</p>
                          <p className="text-zinc-400 text-xs">{r.email}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Recipients */}
              {selectedRecipients.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-zinc-300">Selecionados ({selectedRecipients.length})</Label>
                    <button onClick={() => setSelectedRecipients([])} className="text-red-400 hover:text-red-300 text-xs" data-testid="clear-selected-recipients">Limpar</button>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedRecipients.map((r) => (
                      <div key={r.email} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2 border border-white/5">
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">{r.name}</p>
                          <p className="text-zinc-400 text-xs truncate">{r.email}</p>
                        </div>
                        <button onClick={() => removeRecipient(r.email)} className="text-zinc-500 hover:text-red-400 ml-2 flex-shrink-0" data-testid={`remove-recipient-${r.email}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-amber-400 text-xs">O email será enviado apenas para os destinatários selecionados acima.</p>
                  </div>
                </div>
              )}

              {/* Divider when no specific recipients */}
              {selectedRecipients.length === 0 && (
                <>
                  <div className="flex items-center gap-2 my-1">
                    <div className="flex-1 border-t border-white/10"></div>
                    <span className="text-zinc-500 text-xs">ou enviar para todos</span>
                    <div className="flex-1 border-t border-white/10"></div>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Audiência</Label>
                    <Select value={audience} onValueChange={setAudience}>
                      <SelectTrigger className="input-dark mt-2" data-testid="email-audience-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="buyers" className="text-white">Compradores</SelectItem>
                        <SelectItem value="users" className="text-white">Utilizadores registados</SelectItem>
                        <SelectItem value="both" className="text-white">Utilizadores e Compradores</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-zinc-500 text-xs mt-1">
                      {audience === "buyers" && "Só quem comprou/reservou bilhetes."}
                      {audience === "users" && "Só quem tem conta registada na plataforma."}
                      {audience === "both" && "Ambos, sem duplicações de email."}
                    </p>
                  </div>
                  <div className={audience === "users" ? "opacity-50 pointer-events-none" : ""}>
                    <Label className="text-zinc-300">Sessão</Label>
                    <Select value={sessionFilter} onValueChange={setSessionFilter} disabled={audience === "users"}>
                      <SelectTrigger className="input-dark mt-2" data-testid="email-session-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="all" className="text-white">Todas as sessões</SelectItem>
                        {sessions.map((s) => {
                          const parts = [];
                          if (s.name) parts.push(s.name);
                          if (s.subtitle) parts.push(s.subtitle);
                          if (s.date) parts.push(s.date);
                          if (s.time) parts.push(s.time);
                          return (
                            <SelectItem key={s.id} value={s.id} className="text-white">
                              {parts.join(" · ") || "(sessão sem nome)"}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={audience === "users" ? "opacity-50 pointer-events-none" : ""}>
                    <Label className="text-zinc-300">Estado</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter} disabled={audience === "users"}>
                      <SelectTrigger className="input-dark mt-2" data-testid="email-status-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="all" className="text-white">Todos</SelectItem>
                        <SelectItem value="paid" className="text-white">Pagos</SelectItem>
                        <SelectItem value="pending" className="text-white">Pendentes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-white" data-testid="subscriber-count">{selectedRecipients.length > 0 ? selectedRecipients.length : subscriberCount}</p>
                <p className="text-zinc-500 text-sm">{selectedRecipients.length > 0 ? "selecionados" : "emails únicos"}</p>
              </div>
            </div>
          </div>

          {/* Email History */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Histórico</h2>
              {history.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="text-red-400 hover:text-red-300 text-xs" data-testid="clear-email-history">
                      Limpar
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-900 border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Limpar Histórico</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        Tem a certeza? Todo o histórico de emails enviados será apagado.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={async () => {
                          try {
                            await axios.delete(`${API_URL}/api/admin/emails/history`, { withCredentials: true });
                            toast.success("Histórico limpo");
                            fetchHistory();
                          } catch { toast.error("Erro ao limpar histórico"); }
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Limpar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            {history.length === 0 ? (
              <p className="text-zinc-500 text-sm">Nenhum email enviado ainda</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="bg-zinc-800/50 rounded-lg p-3 border border-white/5 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{h.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-green-400 text-xs">{h.sent} enviados</span>
                        {h.failed > 0 && (
                          <button
                            type="button"
                            onClick={() => openFailures(h)}
                            className="text-red-400 text-xs underline decoration-dotted hover:text-red-300 hover:decoration-solid"
                            data-testid={`btn-show-failures-${h.id}`}
                          >
                            {h.failed} falharam · ver detalhes
                          </button>
                        )}
                      </div>
                      <p className="text-zinc-500 text-xs mt-1">{new Date(h.sent_at).toLocaleString("pt-PT")}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => duplicateCampaign(h.id)}
                        className="text-zinc-500 hover:text-amber-400 transition-colors p-1"
                        title="Duplicar — carregar no editor"
                        data-testid={`duplicate-email-${h.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await axios.delete(`${API_URL}/api/admin/emails/history/${h.id}`, { withCredentials: true });
                            fetchHistory();
                          } catch { toast.error("Erro ao eliminar"); }
                        }}
                        className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                        title="Eliminar"
                        data-testid={`delete-email-${h.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WhatsApp Section */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <h2 className="text-lg font-semibold text-white mb-4">Conversar via WhatsApp</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Procurar Destinatário</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={waSearch}
                  onChange={(e) => { setWaSearch(e.target.value); searchWaRecipient(e.target.value); }}
                  className="input-dark pl-9"
                  placeholder="Nome, email ou telefone..."
                  data-testid="wa-recipient-search"
                />
                {waSearchOpen && waResults.length > 0 && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-zinc-800 border border-white/10 rounded-lg max-h-48 overflow-y-auto shadow-xl">
                    {waResults.map((r) => (
                      <button
                        key={r.email}
                        onClick={() => { setWaRecipient(r); setWaSearch(""); setWaResults([]); setWaSearchOpen(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-700 transition-colors border-b border-white/5 last:border-0"
                      >
                        <p className="text-white text-sm">{r.name}</p>
                        <p className="text-zinc-400 text-xs">{r.email} {r.phone ? `| ${r.phone}` : ""}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {waRecipient && (
              <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2 border border-white/5">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{waRecipient.name}</p>
                  <p className="text-zinc-400 text-xs">{waRecipient.phone || "Sem telefone"}</p>
                </div>
                <button onClick={() => setWaRecipient(null)} className="text-zinc-500 hover:text-red-400 ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Mensagem</Label>
              <textarea
                value={waMessage}
                onChange={(e) => setWaMessage(e.target.value)}
                className="input-dark mt-2 w-full min-h-[100px] rounded-lg p-3 resize-y"
                placeholder="Escreva a sua mensagem..."
                data-testid="wa-message"
              />
            </div>
            <Button
              onClick={() => {
                if (!waRecipient) { toast.error("Selecione um destinatário"); return; }
                const phone = (waRecipient.phone || "").replace(/\D/g, "");
                if (!phone) { toast.error("Este destinatário não tem número de telefone"); return; }
                const msg = encodeURIComponent(waMessage || `Olá ${waRecipient.name}`);
                window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
                setWaMessage("");
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              disabled={!waRecipient}
              data-testid="wa-send-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Enviar via WhatsApp
            </Button>
          </div>
        </div>
      </div>

      {/* Failures Modal */}
      <Dialog open={!!failuresModal} onOpenChange={(v) => { if (!v) setFailuresModal(null); }}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-failures-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Emails que falharam
            </DialogTitle>
          </DialogHeader>
          {failuresModal && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-400 truncate">
                Campanha: <span className="text-white">{failuresModal.subject}</span>
              </div>
              {loadingFailures ? (
                <div className="text-zinc-500 text-sm">A carregar...</div>
              ) : failuresModal.failed_recipients.length === 0 ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-100 text-sm">
                  ✅ Nenhuma falha pendente.
                </div>
              ) : (
                <>
                  <div className="bg-zinc-950 border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-900 text-zinc-400">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Email</th>
                          <th className="text-left px-3 py-2 font-medium">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {failuresModal.failed_recipients.map((f, i) => (
                          <tr key={`${f.email}-${i}`} className="border-t border-white/5">
                            <td className="px-3 py-2 font-mono text-zinc-200 break-all">{f.email}</td>
                            <td className="px-3 py-2 text-red-300">{f.error || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button onClick={exportFailuresCsv} variant="outline" className="border-white/10 text-zinc-300" data-testid="btn-export-failures-csv">
                      <Download className="w-4 h-4 mr-2" /> Exportar CSV
                    </Button>
                    <Button onClick={retryFailed} disabled={retrying} className="btn-primary" data-testid="btn-retry-failures">
                      <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? "animate-spin" : ""}`} />
                      {retrying ? "A reenviar..." : `Reenviar ${failuresModal.failed_recipients.length}`}
                    </Button>
                  </div>
                  <p className="text-zinc-500 text-xs">
                    💡 <strong>Causas comuns:</strong> erros de digitação no domínio (ex.: <code>.cpom</code> em vez de <code>.com</code>), endereço em <em>suppression list</em> da Resend após bounce anterior, ou limite de envio momentâneo.
                  </p>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

